import React, { createContext, useContext, useReducer, useCallback } from 'react';
import api from '../config/api';
import endpoints from '../config/endpoints';
import { applyActionCode } from 'firebase/auth';
import { auth } from '../config/firebase';
import {
    signInWithPopup,
    GoogleAuthProvider,
    signOut,
    signInAnonymously as firebaseSignInAnonymously,
    linkWithPopup,
    linkWithCredential,
    EmailAuthProvider
} from 'firebase/auth';

const AuthContext = createContext(null);

export const STORAGE_KEYS = {
    TOKEN: 'auth_token',
    REFRESH_TOKEN: 'auth_refresh_token',
    USER: 'auth_user',
};

const getStoredAuth = () => {
    try {
        const token = localStorage.getItem(STORAGE_KEYS.TOKEN);
        const storedUser = localStorage.getItem(STORAGE_KEYS.USER);
        const user = storedUser ? JSON.parse(storedUser) : null;

        return {
            token,
            user,
            isAuthenticated: !!token && !!user,
            isLoading: false,
            error: null,
        };
    } catch (error) {
        console.error('Error reading auth from localStorage:', error);
        return {
            token: null,
            user: null,
            isAuthenticated: false,
            isLoading: false,
            error: null,
        };
    }
};

const initialState = getStoredAuth();

const userProfileShape = {
    uid: '',
    email: '',
    email_verified: false,
    name: '',
    created_at: '',
    updated_at: '',
    is_anonymous: false,
    preferences: {
        currency: '',
        timezone: '',
        budget: 0,
        notification_settings: {
            email_reminders: false,
            push_notifications: false
        }
    }
};

const fetchUserProfile = async () => {
    try {
        const token = localStorage.getItem(STORAGE_KEYS.TOKEN);
        if (!token) {
            throw new Error('No authentication token found');
        }

        // Fetch both profile and status in parallel
        const [profileResponse, statusResponse] = await Promise.all([
            api.get(endpoints.user.profile),
            api.get(endpoints.auth.userStatus)
        ]);

        // Merge the profile and status data
        const userProfile = {
            ...profileResponse.data,
            is_anonymous: statusResponse.data.is_anonymous,
            auth_provider: statusResponse.data.auth_provider,
            email_verified: statusResponse.data.email_verified,
            has_email: statusResponse.data.has_email,
        };

        return userProfile;
    } catch (error) {
        console.error('Error fetching user profile:', error);
        // If we get a 401, the token is invalid or expired
        if (error.response?.status === 401) {
            localStorage.removeItem(STORAGE_KEYS.TOKEN);
            localStorage.removeItem(STORAGE_KEYS.USER);
            window.location.href = '/login';
        }
        throw error;
    }
};

const authReducer = (state, action) => {
    switch (action.type) {
        case 'AUTH_START':
            return {
                ...state,
                isLoading: true,
                error: null,
            };
        case 'AUTH_SUCCESS':
            return {
                ...state,
                user: action.payload.user,
                token: action.payload.token,
                isAuthenticated: true,
                isLoading: false,
                error: null,
            };
        case 'AUTH_FAIL':
            return {
                ...state,
                isLoading: false,
                error: action.payload,
            };
        case 'LOGOUT':
            return {
                ...initialState,
                token: null,
            };
        case 'CLEAR_ERROR':
            return {
                ...state,
                error: null,
            };
        case 'UPDATE_PROFILE':
            return {
                ...state,
                user: {
                    ...state.user,
                    ...action.payload,
                    preferences: {
                        ...state.user?.preferences,
                        ...action.payload.preferences
                    }
                },
            };
        case 'UPDATE_USER_STATUS':
            return {
                ...state,
                user: {
                    ...state.user,
                    is_anonymous: action.payload.is_anonymous,
                    auth_provider: action.payload.auth_provider,
                    email_verified: action.payload.email_verified,
                    has_email: action.payload.has_email,
                }
            };
        default:
            return state;
    }
};

const handleApiError = (error) => {
    if (error.response) {
        // Server responded with error
        switch (error.response.status) {
            case 401:
                return 'Invalid credentials. Please check your email and password.';
            case 403:
                return 'Access forbidden. Please log in again.';
            case 500:
                return 'Server error. Please try again later.';
            default:
                return error.response.data?.message || 'An unexpected error occurred';
        }
    } else if (error.request) {
        // Request made but no response
        return 'Unable to reach the server. Please check your internet connection.';
    } else {
        // Error in request setup
        return 'An error occurred while setting up the request.';
    }
};

export const AuthProvider = ({ children }) => {
    const [state, dispatch] = useReducer(authReducer, initialState);

    const login = useCallback(async (email, password) => {
        try {
            dispatch({ type: 'AUTH_START' });

            // Regular login flow (no more anonymous linking here)
            const response = await api.post(endpoints.auth.login, {
                email,
                password,
            });

            const token = response.data.token || response.data.access_token;
            const refreshToken = response.data.refresh_token || response.data.refreshToken;

            if (!token || !refreshToken) {
                throw new Error('Invalid token response from server');
            }

            // Store both tokens
            localStorage.setItem(STORAGE_KEYS.TOKEN, token);
            localStorage.setItem(STORAGE_KEYS.REFRESH_TOKEN, refreshToken);

            // Then fetch user profile
            const userProfile = await fetchUserProfile();

            // Store user profile after successful fetch
            localStorage.setItem(STORAGE_KEYS.USER, JSON.stringify(userProfile));

            dispatch({
                type: 'AUTH_SUCCESS',
                payload: {
                    user: userProfile,
                    token: token
                },
            });
        } catch (error) {
            console.error('Login error:', error);
            const errorMessage = handleApiError(error);
            dispatch({
                type: 'AUTH_FAIL',
                payload: errorMessage,
            });
            throw new Error(errorMessage);
        }
    }, []);

    const signup = useCallback(async (email, password, name, isAnonymousConversion = false) => {
        try {
            dispatch({ type: 'AUTH_START' });

            if (auth.currentUser?.isAnonymous) {
                // First link the account in Firebase
                const credential = EmailAuthProvider.credential(email, password);
                await linkWithCredential(auth.currentUser, credential);

                // Then notify backend about the linked account
                const response = await api.post(endpoints.auth.linkAccount, {
                    email,
                    name,
                    auth_provider: 'email'
                });

                const { access_token, refresh_token } = response.data;

                // Store tokens
                localStorage.setItem(STORAGE_KEYS.TOKEN, access_token);
                localStorage.setItem(STORAGE_KEYS.REFRESH_TOKEN, refresh_token);

                // Fetch and store user profile
                const userProfile = await fetchUserProfile();
                localStorage.setItem(STORAGE_KEYS.USER, JSON.stringify(userProfile));

                dispatch({
                    type: 'AUTH_SUCCESS',
                    payload: {
                        user: userProfile,
                        token: access_token
                    },
                });
                return response.data;
            } else {
                // Regular signup for new users
                const response = await api.post(endpoints.auth.signup, {
                    email,
                    password,
                    name
                });
                dispatch({ type: 'AUTH_SUCCESS', payload: { user: null, token: null } });
                return response.data;
            }
        } catch (error) {
            console.error('Signup error:', error);
            const errorMessage = handleApiError(error);
            dispatch({
                type: 'AUTH_FAIL',
                payload: errorMessage,
            });
            throw new Error(errorMessage);
        }
    }, []);

    const logout = useCallback(async () => {
        try {
            await api.post(endpoints.auth.logout);
            await signOut(auth);
        } catch (error) {
            console.warn('Logout error:', error);
        } finally {
            localStorage.removeItem(STORAGE_KEYS.TOKEN);
            localStorage.removeItem(STORAGE_KEYS.REFRESH_TOKEN);
            localStorage.removeItem(STORAGE_KEYS.USER);
            dispatch({ type: 'LOGOUT' });
            window.location.href = '/login';
        }
    }, []);

    const clearError = useCallback(() => {
        dispatch({ type: 'CLEAR_ERROR' });
    }, []);

    const updateUserProfile = useCallback(async () => {
        try {
            const userProfile = await fetchUserProfile();

            localStorage.setItem(STORAGE_KEYS.USER, JSON.stringify(userProfile));
            dispatch({
                type: 'UPDATE_PROFILE',
                payload: userProfile,
            });

            return userProfile;
        } catch (error) {
            const errorMessage = handleApiError(error);
            dispatch({
                type: 'AUTH_FAIL',
                payload: errorMessage,
            });
            throw new Error(errorMessage);
        }
    }, []);

    const verifyEmail = useCallback(async (oobCode) => {
        try {
            await applyActionCode(auth, oobCode);
            return true;
        } catch (error) {
            console.error('Email verification error:', error);
            throw new Error('Failed to verify email. The link may be invalid or expired.');
        }
    }, []);

    const signInWithGoogle = useCallback(async () => {
        try {
            dispatch({ type: 'AUTH_START' });
            const provider = new GoogleAuthProvider();

            console.log('Current user status:', {
                exists: !!auth.currentUser,
                isAnonymous: auth.currentUser?.isAnonymous,
                uid: auth.currentUser?.uid
            });

            console.log("USER STATUS", auth?.currentUser);

            let result;
            if (auth.currentUser?.isAnonymous) {
                console.log('Attempting to link anonymous user with Google');
                // Link anonymous user with Google account
                result = await linkWithPopup(auth.currentUser, provider);
                console.log('Link successful:', {
                    email: result.user.email,
                    uid: result.user.uid,
                    isAnonymous: result.user.isAnonymous
                });

                console.log('Calling linkAccount endpoint');
                const response = await api.post(endpoints.auth.linkAccount, {
                    email: result.user.email,
                    name: result.user.displayName || '',
                    profile_picture: result.user.photoURL || '',
                    auth_provider: 'google'
                });
                console.log('linkAccount response:', response.data);

                const { access_token, refresh_token } = response.data;

                // Store tokens
                localStorage.setItem(STORAGE_KEYS.TOKEN, access_token);
                localStorage.setItem(STORAGE_KEYS.REFRESH_TOKEN, refresh_token);

                // Fetch and store user profile
                const userProfile = await fetchUserProfile();
                localStorage.setItem(STORAGE_KEYS.USER, JSON.stringify(userProfile));

                dispatch({
                    type: 'AUTH_SUCCESS',
                    payload: {
                        user: userProfile,
                        token: access_token
                    },
                });
            } else {
                console.log('Regular Google sign in flow');
                result = await signInWithPopup(auth, provider);
                const idToken = await result.user.getIdToken();
                console.log('Calling googleLogin endpoint');
                const response = await api.post(endpoints.auth.googleLogin, { id_token: idToken });
                console.log('googleLogin response:', response.data);

                const { access_token, refresh_token } = response.data;

                // Store tokens
                localStorage.setItem(STORAGE_KEYS.TOKEN, access_token);
                localStorage.setItem(STORAGE_KEYS.REFRESH_TOKEN, refresh_token);

                // Fetch and store user profile
                const userProfile = await fetchUserProfile();
                localStorage.setItem(STORAGE_KEYS.USER, JSON.stringify(userProfile));

                dispatch({
                    type: 'AUTH_SUCCESS',
                    payload: {
                        user: userProfile,
                        token: access_token
                    },
                });
            }
        } catch (error) {
            console.error('Google Sign-in error:', {
                message: error.message,
                code: error.code,
                fullError: error
            });
            const errorMessage = handleApiError(error);
            dispatch({
                type: 'AUTH_FAIL',
                payload: errorMessage,
            });
            throw new Error(errorMessage);
        }
    }, []);

    const signInAnonymously = useCallback(async () => {
        try {
            dispatch({ type: 'AUTH_START' });
            const result = await firebaseSignInAnonymously(auth);
            const idToken = await result.user.getIdToken();

            // Exchange Firebase token with our backend
            const response = await api.post(endpoints.auth.anonymousLogin, {
                id_token: idToken
            });

            const { access_token, refresh_token, is_anonymous } = response.data;

            if (!access_token || !refresh_token) {
                throw new Error('Invalid token response from server');
            }

            // Store tokens
            localStorage.setItem(STORAGE_KEYS.TOKEN, access_token);
            localStorage.setItem(STORAGE_KEYS.REFRESH_TOKEN, refresh_token);

            // Fetch user profile
            const userProfile = await fetchUserProfile();
            const updatedProfile = {
                ...userProfile,
                is_anonymous
            };

            localStorage.setItem(STORAGE_KEYS.USER, JSON.stringify(updatedProfile));

            dispatch({
                type: 'AUTH_SUCCESS',
                payload: {
                    user: updatedProfile,
                    token: access_token
                },
            });

            return updatedProfile;
        } catch (error) {
            console.error('Anonymous Sign-in error:', error);
            const errorMessage = handleApiError(error);
            dispatch({
                type: 'AUTH_FAIL',
                payload: errorMessage,
            });
            throw new Error(errorMessage);
        }
    }, []);

    const value = {
        ...state,
        login,
        signup,
        logout,
        clearError,
        updateUserProfile,
        verifyEmail,
        signInWithGoogle,
        signInAnonymously,
    };

    return (
        <AuthContext.Provider value={value}>
            {children}
        </AuthContext.Provider>
    );
};

export const useAuth = () => {
    const context = useContext(AuthContext);
    if (!context) {
        throw new Error('useAuth must be used within an AuthProvider');
    }
    return context;
}; 